import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { extractPDFAreaFromPage } from '../../utils/pdfExtractor';

const PieceCropEditor = ({ piece, onSave, onCancel }) => {
  const { t } = useLanguage();
  const [cropLines, setCropLines] = useState({
    left: 0,
    right: piece.width,
    top: 0,
    bottom: piece.height
  });
  const [draggingLine, setDraggingLine] = useState(null); // 'left', 'right', 'top', 'bottom'
  const [canvasLoaded, setCanvasLoaded] = useState(false);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    // Draw the piece image on the canvas
    if (canvasRef.current && piece.extractedImage) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        // Initialize crop lines to full image bounds with small inset for easier grabbing
        const inset = 20; // 20px inset from edges
        setCropLines({
          left: inset,
          right: img.width - inset,
          top: inset,
          bottom: img.height - inset
        });

        // Mark canvas as loaded to trigger line rendering
        setCanvasLoaded(true);
      };

      img.src = piece.extractedImage;
    }
  }, [piece.extractedImage]);

  const startDragLine = (line) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingLine(line);
  };

  const updateDragLine = (e) => {
    if (!draggingLine || !canvasRef.current) return;
    e.preventDefault();
    e.stopPropagation();

    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    if (clientX === undefined || clientY === undefined) return;

    const x = clientX - rect.left;
    const y = clientY - rect.top;

    // Convert to canvas coordinates
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;

    const canvasX = x * scaleX;
    const canvasY = y * scaleY;

    setCropLines(prev => {
      const newLines = { ...prev };

      if (draggingLine === 'left') {
        newLines.left = Math.max(0, Math.min(canvasX, prev.right - 10));
      } else if (draggingLine === 'right') {
        newLines.right = Math.min(canvasRef.current.width, Math.max(canvasX, prev.left + 10));
      } else if (draggingLine === 'top') {
        newLines.top = Math.max(0, Math.min(canvasY, prev.bottom - 10));
      } else if (draggingLine === 'bottom') {
        newLines.bottom = Math.min(canvasRef.current.height, Math.max(canvasY, prev.top + 10));
      }

      return newLines;
    });
  };

  const finishDragLine = () => {
    setDraggingLine(null);
  };

  const handleSave = async () => {
    const cropWidth = cropLines.right - cropLines.left;
    const cropHeight = cropLines.bottom - cropLines.top;

    if (cropWidth < 10 || cropHeight < 10) {
      alert(t('cropAreaTooSmall') || 'Crop area too small');
      return;
    }

    try {
      // Create a new canvas with the cropped area
      const sourceCanvas = canvasRef.current;
      const croppedCanvas = document.createElement('canvas');
      const ctx = croppedCanvas.getContext('2d');

      croppedCanvas.width = cropWidth;
      croppedCanvas.height = cropHeight;

      // Draw the cropped portion
      ctx.drawImage(
        sourceCanvas,
        cropLines.left, cropLines.top, cropWidth, cropHeight,
        0, 0, cropWidth, cropHeight
      );

      // Convert to high-quality data URL
      const croppedImage = croppedCanvas.toDataURL('image/png', 1.0);

      // Create updated piece with cropped image
      const updatedPiece = {
        ...piece,
        width: cropWidth,
        height: cropHeight,
        extractedImage: croppedImage
      };

      onSave(updatedPiece);
    } catch (error) {
      console.error('Error cropping piece:', error);
      alert(t('cropError') || 'Error cropping image');
    }
  };

  const getLineStyle = (line) => {
    if (!canvasRef.current || !containerRef.current) return {};

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    const displayScaleX = rect.width / canvas.width;
    const displayScaleY = rect.height / canvas.height;

    const baseStyle = {
      position: 'absolute',
      backgroundColor: 'transparent',
      zIndex: 15,
      cursor: line === 'left' || line === 'right' ? 'ew-resize' : 'ns-resize'
    };

    const hitAreaWidth = 8; // Wider hit area for easier grabbing
    const lineWidth = 1; // Thin line
    const dashSize = 16; // Size of each dash
    const dashColor = "#FF2D34"

    // Get canvas offset within container (accounting for scroll)
    const canvasOffsetLeft = canvas.offsetLeft;
    const canvasOffsetTop = canvas.offsetTop;

    if (line === 'left' || line === 'right') {
      const x = cropLines[line] * displayScaleX;
      return {
        ...baseStyle,
        left: (canvasOffsetLeft + x - hitAreaWidth / 2) + 'px',
        top: canvasOffsetTop + 'px',
        width: hitAreaWidth + 'px',
        height: rect.height + 'px',
        // Thin dotted line in the center of the hit area
        backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent ${dashSize}px, ${dashColor} ${dashSize}px, ${dashColor} ${dashSize * 2}px), linear-gradient(to right, transparent ${(hitAreaWidth - lineWidth) / 2}px, rgba(255,0,0,0.3) ${(hitAreaWidth - lineWidth) / 2}px, rgba(255,0,0,0.3) ${(hitAreaWidth + lineWidth) / 2}px, transparent ${(hitAreaWidth + lineWidth) / 2}px)`,
        backgroundSize: `100% 100%, 100% 100%`,
        backgroundPosition: '0 0, 0 0',
        opacity: 0.4,
      };
    } else {
      const y = cropLines[line] * displayScaleY;
      return {
        ...baseStyle,
        left: canvasOffsetLeft + 'px',
        top: (canvasOffsetTop + y - hitAreaWidth / 2) + 'px',
        width: rect.width + 'px',
        height: hitAreaWidth + 'px',
        // Thin dotted line in the center of the hit area
        backgroundImage: `repeating-linear-gradient(to right, transparent 0, transparent ${dashSize}px, ${dashColor} ${dashSize}px, ${dashColor} ${dashSize * 2}px), linear-gradient(to bottom, transparent ${(hitAreaWidth - lineWidth) / 2}px, rgba(255,0,0,0.3) ${(hitAreaWidth - lineWidth) / 2}px, rgba(255,0,0,0.3) ${(hitAreaWidth + lineWidth) / 2}px, transparent ${(hitAreaWidth + lineWidth) / 2}px)`,
        backgroundSize: `100% 100%, 100% 100%`,
        backgroundPosition: '0 0, 0 0',
        opacity: 0.4,
      };
    }
  };

  const getCropAreaStyle = () => {
    if (!canvasRef.current || !containerRef.current) return {};

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    const displayScaleX = rect.width / canvas.width;
    const displayScaleY = rect.height / canvas.height;

    // Get canvas offset within container
    const canvasOffsetLeft = canvas.offsetLeft;
    const canvasOffsetTop = canvas.offsetTop;

    const left = cropLines.left * displayScaleX;
    const top = cropLines.top * displayScaleY;
    const width = (cropLines.right - cropLines.left) * displayScaleX;
    const height = (cropLines.bottom - cropLines.top) * displayScaleY;

    return {
      position: 'absolute',
      left: (canvasOffsetLeft + left) + 'px',
      top: (canvasOffsetTop + top) + 'px',
      width: width + 'px',
      height: height + 'px',
      border: '2px solid #00ff00',
      backgroundColor: 'rgba(0, 255, 0, 0.1)',
      pointerEvents: 'none',
      zIndex: 10
    };
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        maxWidth: '80vw',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <h3 style={{ marginTop: 0 }}>{t('cropPiece') || 'Crop Piece'}</h3>
        <p style={{ marginBottom: '15px', color: '#666' }}>
          {t('dragLinesToCrop') || 'Drag the red lines to define the crop area'}
        </p>

        <div
          ref={containerRef}
          style={{
            position: 'relative',
            flex: 1,
            overflow: draggingLine ? 'hidden' : 'auto',
            border: '1px solid #ddd',
            borderRadius: '4px',
            marginBottom: '15px',
            //maxWidth: '600px',
            minHeight: '400px',
            touchAction: draggingLine ? 'none' : 'auto'
          }}
          onMouseMove={updateDragLine}
          onMouseUp={finishDragLine}
          onMouseLeave={finishDragLine}
          onTouchMove={updateDragLine}
          onTouchEnd={finishDragLine}
        >
          <canvas
            ref={canvasRef}
            style={{
              display: 'block',
              maxWidth: '100%',
              height: 'auto'
            }}
          />

          {/* Crop area highlight and lines - only show when canvas is loaded */}
          {canvasLoaded && (
            <>
              <div style={getCropAreaStyle()} />

              {/* Draggable lines */}
              <div
                style={{...getLineStyle('left'), touchAction: 'none'}}
                onMouseDown={startDragLine('left')}
                onTouchStart={startDragLine('left')}
              />
              <div
                style={{...getLineStyle('right'), touchAction: 'none'}}
                onMouseDown={startDragLine('right')}
                onTouchStart={startDragLine('right')}
              />
              <div
                style={{...getLineStyle('top'), touchAction: 'none'}}
                onMouseDown={startDragLine('top')}
                onTouchStart={startDragLine('top')}
              />
              <div
                style={{...getLineStyle('bottom'), touchAction: 'none'}}
                onMouseDown={startDragLine('bottom')}
                onTouchStart={startDragLine('bottom')}
              />
            </>
          )}
        </div>

        <div style={{
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              borderRadius: '24px',
              //backgroundColor: "#E3F2FD",
              color: "#2ba5f7",
              border: '1px solid #2ba5f7',
              backgroundColor: 'white',
              cursor: 'pointer',
              //color: '#333',
              fontSize: '14px'
            }}
          >
            {t('cancel') || 'Cancel'}
          </button>
          <button
            onClick={handleSave}
            disabled={(cropLines.right - cropLines.left) < 10 || (cropLines.bottom - cropLines.top) < 10}
            style={{
              padding: '10px 20px',
              borderRadius: '24px',
              border: 'none',
              backgroundColor: ((cropLines.right - cropLines.left) < 10 || (cropLines.bottom - cropLines.top) < 10) ? '#ccc' : '#2196F3',
              color: 'white',
              cursor: ((cropLines.right - cropLines.left) < 10 || (cropLines.bottom - cropLines.top) < 10) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {t('saveCrop') || 'Save Crop'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PieceCropEditor;
